import {
  Editor,
  Node,
  Range,
  Element as SlateElement,
  type Element as SlateElementType,
  Text,
  Transforms,
} from 'slate';

import type {
  CustomEditor,
  CustomElement,
  LinkElement,
} from '../types/SlateNodes';

const URL_START_REGEX = /^https?:\/\/\S*$/i;
const URL_PASTE_REGEX =
  /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)/g;

/**
 * Slate plugin that auto-promotes http(s) URLs typed inline (and
 * pasted in bulk) into `link` elements. Direct port of PopshopRN's
 * `withLinks`. Links are inline elements; typing a space or newline
 * inside a link moves the cursor *out* of the link so the trailing
 * text stays plain.
 *
 * The link element's `url` field is kept in sync with its text on
 * each `onChange` — typing into a link extends the URL.
 *
 * Element narrowing uses local casts rather than Slate's
 * `declare module 'slate'` augmentation — another workspace slice
 * (TemplateEditor) already augments globally.
 */
export function withLinks(editor: CustomEditor): CustomEditor {
  const { insertData, insertText, isInline, onChange } = editor;

  editor.isInline = element =>
    (element as CustomElement).type === 'link' ? true : isInline(element);

  editor.insertText = text => {
    if ((text === ' ' || text === '\n') && isInsideLink(editor)) {
      moveOutsideLink(editor);
      insertText(text);
      return;
    }
    insertText(text);
    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      convertTextToLinkIfNeeded(editor);
      updateLinkUrl(editor);
    }
  };

  editor.onChange = () => {
    updateLinkUrl(editor);
    onChange();
  };

  editor.insertData = data => {
    const text = data.getData('text/plain');
    if (text) {
      insertTextWithLinks(editor, text);
      return;
    }
    insertData(data);
  };

  return editor;
}

function isUrl(s: string): boolean {
  return URL_START_REGEX.test(s);
}

function isLink(n: SlateElementType): boolean {
  return (n as CustomElement).type === 'link';
}

function isInsideLink(editor: Editor): boolean {
  const [link] = Editor.nodes(editor, {
    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && isLink(n),
  });
  return !!link;
}

function moveOutsideLink(editor: Editor) {
  if (!editor.selection) return;
  const [linkNode, linkPath] =
    Editor.nodes(editor, {
      match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && isLink(n),
    }).next().value || [];
  if (linkNode && linkPath) {
    const afterPoint = Editor.after(editor, linkPath);
    if (afterPoint) Transforms.select(editor, afterPoint);
  }
}

function convertTextToLinkIfNeeded(editor: Editor) {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return;

  const [node, path] = Editor.node(editor, selection);
  if (!Text.isText(node)) return;
  const text = node.text;
  const cursorOffset = selection.anchor.offset;

  let start = cursorOffset - 1;
  while (start >= 0 && !/\s/.test(text[start])) {
    start--;
  }
  start++;
  const currentWord = text.slice(start, cursorOffset);

  if (!isUrl(currentWord) || isInsideLink(editor)) return;

  const [existingLink] = Editor.nodes(editor, {
    at: {
      anchor: { path, offset: start },
      focus: { path, offset: cursorOffset },
    },
    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && isLink(n),
  });
  if (existingLink) return;

  const urlRange = {
    anchor: { path, offset: start },
    focus: { path, offset: cursorOffset },
  };
  Transforms.select(editor, urlRange);
  const link: LinkElement = {
    type: 'link',
    url: currentWord,
    children: [{ text: currentWord }],
  };
  Transforms.wrapNodes(editor, link as unknown as SlateElementType, {
    split: true,
  });
  Transforms.collapse(editor, { edge: 'end' });
}

function updateLinkUrl(editor: Editor) {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return;

  const [linkNode, linkPath] =
    Editor.nodes(editor, {
      match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && isLink(n),
    }).next().value || [];
  if (!linkNode || !linkPath) return;

  const linkText = Node.string(linkNode);
  const link = linkNode as unknown as LinkElement;
  if (linkText !== link.url) {
    Transforms.setNodes(
      editor,
      { url: linkText } as unknown as Partial<SlateElementType>,
      { at: linkPath },
    );
  }
}

function insertTextWithLinks(editor: CustomEditor, text: string) {
  const matches: { url: string; start: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  const regex = new RegExp(URL_PASTE_REGEX);
  while ((m = regex.exec(text)) !== null) {
    matches.push({ url: m[0], start: m.index, end: m.index + m[0].length });
  }

  const nodes: (Text | LinkElement)[] = [];
  let lastIndex = 0;
  for (const match of matches) {
    if (match.start > lastIndex) {
      const before = text.slice(lastIndex, match.start);
      if (before) nodes.push({ text: before } as Text);
    }
    nodes.push({
      type: 'link',
      url: match.url,
      children: [{ text: match.url }],
    });
    lastIndex = match.end;
  }
  if (lastIndex < text.length) {
    const remaining = text.slice(lastIndex);
    if (remaining) nodes.push({ text: remaining } as Text);
  }
  if (nodes.length === 0) nodes.push({ text } as Text);
  Transforms.insertNodes(
    editor,
    nodes as unknown as (Text | SlateElementType)[],
  );
}
